import { useState } from 'react'

import { Badge, Box, Divider, Modal, MultiSelect } from '@mantine/core'
import { DateInput } from '@mantine/dates'

import { useParams } from '@redwoodjs/router'
import { useMutation, useQuery } from '@redwoodjs/web'

import Button from 'src/components/Buttons/Button/Button'
import StyledAlert from 'src/components/Feedback/StyledAlert/StyledAlert'
import {
  ASSIGN_TRAININGS,
  GET_APPLICANT_TRAININGS,
  GET_COURSES,
} from 'src/graphql/trainings.graphql'
import { cn } from 'src/utils'

import { toast } from '../Toast/Toast'

import { handleAssignTrainingsCompleted } from './handleAssignTrainingsCompleted'

const CoursesModal = ({ opened, close }) => {
  const [selectedCourses, setSelectedCourses] = useState([])
  const [deadline, setDeadline] = useState()
  const params = useParams()
  const applicantId = params.id

  const { data: availableData, loading: availableLoading } =
    useQuery(GET_COURSES)

  const [assignTrainings, { loading: assignLoading }] = useMutation(
    ASSIGN_TRAININGS,
    {
      onCompleted: (data) => {
        const items = availableData?.courses?.items ?? []
        const labelById = Object.fromEntries(
          items.map((c) => [c.courseId, c.name])
        )
        handleAssignTrainingsCompleted(data, close, labelById)
      },
      onError: (error) => {
        toast(error.message, 'error')
      },
      refetchQueries: [
        {
          query: GET_APPLICANT_TRAININGS,
          variables: {
            applicantId: applicantId,
          },
        },
      ],
    }
  )

  const availableTrainings = availableData?.courses?.items || []

  const courseData = availableTrainings.map((course) => {
    const isLuma = course.provider === 'LUMA'
    return {
      label: course.name,
      value: course.courseId,
      description: course.description,
      numberOfTrainings: isLuma
        ? 'Learning Path'
        : course.trainings.length === 1
          ? '1 lesson'
          : `${course.trainings.length} lessons`,
      trainings: course.trainings,
      provider: course.provider,
    }
  })

  const RenderSelectOption = ({ option }) => {
    const [expanded, setExpanded] = useState(false)
    const isLuma = option.provider === 'LUMA'
    const hasLessons = option.trainings && option.trainings.length > 0

    return (
      <div className="flex w-full flex-col gap-1 font-medium text-doubleNickel-gray-900">
        <div className="flex flex-row items-center justify-between gap-2">
          <span>{option.label}</span>
          <div className="flex items-center gap-2">
            <div className="font-normal text-xs whitespace-nowrap">
              {option.numberOfTrainings}
            </div>
            {isLuma ? (
              <Badge size="xs" variant="light" color="orange" title="Luma">
                Luma
              </Badge>
            ) : (
              <Badge size="xs" variant="light" color="blue" title="Trainix">
                Trainix
              </Badge>
            )}
          </div>
        </div>

        <div className="font-normal text-doubleNickel-gray-700">
          <div className="flex flex-col gap-1 font-normal text-doubleNickel-gray-700">
            {option.description && !hasLessons && (
              <div>{option.description}</div>
            )}
            {hasLessons && (
              <>
                <div
                  className={cn(
                    'flex flex-col gap-1',
                    expanded ? 'line-clamp-none' : 'line-clamp-1'
                  )}
                >
                  {option.description && <div>{option.description}</div> || <div>Lessons:</div>}
                  <div className="flex flex-col gap-2 p-2">
                    {option.description && <span>Lessons:</span>}
                    {option.trainings.map((training) => (
                      <div
                        key={training.training.trainingId}
                        className="text-sm font-normal"
                      >
                        • {training.training.name}
                      </div>
                    ))}
                  </div>
                </div>
                <Box
                  className="text-xs font-medium text-doubleNickel-brand-400 hover:cursor-pointer hover:underline"
                  onClick={(e) => {
                    e.stopPropagation()
                    setExpanded(!expanded)
                  }}
                >
                  {expanded ? 'Hide' : 'Show more'}
                </Box>
              </>
            )}
          </div>
        </div>
      </div>
    )
  }

  return (
    <Modal
      opened={opened}
      onClose={close}
      size="45%"
      yOffset="20vh"
      classNames={{ title: 'text-xl font-bold', body: 'pt-4' }}
      title="Assign a course"
    >
      <div className="flex min-h-[300px] flex-col gap-4">
        <MultiSelect
          searchable
          label="Select the courses you would like to assign"
          placeholder="Select"
          hidePickedOptions
          withAsterisk={true}
          required
          data={courseData}
          value={selectedCourses}
          onChange={(value) => setSelectedCourses(value)}
          renderOption={RenderSelectOption}
          maxDropdownHeight={400}
        />
        <DateInput
          label="Deadline"
          placeholder="Select date"
          valueFormat="MM/DD/YYYY"
          maxDate={new Date(new Date().setMonth(new Date().getMonth() + 1))}
          value={deadline}
          onChange={(value) => setDeadline(value)}
        />

        {!availableTrainings.length && !availableLoading && (
          <StyledAlert
            variant="warning"
            description={
              <span data-testid="warning-message">
                No courses available to assign.
                <br />
                Please contact the Double Nickel team to set up your training
                integration!
              </span>
            }
          />
        )}

        <div className="mt-auto">
          <Divider my="md" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Button
            text="Cancel"
            className="text-base"
            variant="outline"
            onClick={close}
          />
          <Button
            text="Confirm"
            className="text-base"
            loading={assignLoading}
            onClick={() => {
              assignTrainings({
                variables: {
                  applicantId: applicantId,
                  courseIds: selectedCourses,
                  deadline: deadline,
                },
              })
            }}
          />
        </div>
      </div>
    </Modal>
  )
}

export default CoursesModal
